import { useRef } from 'react';
import html2pdf from 'html2pdf.js';
import { formatLocalDate } from '../../../utils/formatDate';
import "./styles.css"
import { formatRomaneio } from '../../../utils/formatRomaneio';

export default function NpsViewModal({ onClose, nps, onDelete }) {
    const pdfRef = useRef();

    const handleExportPDF = () => {
        const element = pdfRef.current;
        const opt = {
            margin: 0.5,
            filename: `NPS_${nps.clientName}_${nps.id}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 1.5 },
            jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        };

        html2pdf().from(element).set(opt).save();
    };

    const getBadgeClass = (score) => {
        if (score >= 8) return 'bg-green-100 text-green-800';
        if (score >= 6) return 'bg-yellow-100 text-yellow-800';
        return 'bg-red-100 text-red-800';
    };

    const getRatingClass = (value) => {
        if (value === 3) return 'bg-green-100 text-green-800';
        if (value === 2) return 'bg-indigo-100 text-indigo-800';
        if (value === 1) return 'bg-yellow-100 text-yellow-800';
        return 'bg-red-100 text-red-800';
    };

    const getNpsCategory = (score) => {
        if (score >= 9) return 'Promoter';
        if (score >= 7) return 'Passive';
        return 'Detractor';
    };

    return (
        <div id='viewer-nps' className="fixed inset-0 z-50 overflow-y-auto ">
            <div className="flex items-center justify-center min-h-screen px-4 lg:text-left">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="fade-in inline-block align-bottom bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    <div ref={pdfRef}>
                        <div className="bg-gray-50 flex justify-between items-start p-2">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 px-3">
                                Detalhes de feedback do NPS {nps.id}
                            </h3>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 px-2">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="bg-white pb-4 sm:p-6 sm:pb-4 ">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Informações do Cliente</h4>
                                    <div className="space-y-2 text-sm">
                                        <p><span className="text-gray-600">Nome do Cliente:</span> <span className="font-medium">{nps.clientName}</span></p>
                                        <p><span className="text-gray-600">Código Cliente:</span> <span className="font-medium">{nps.clientCode.substring(4)}</span></p>
                                        <p><span className="text-gray-600">Filial:</span> <span className="font-medium">{nps.branchName} ({nps.branchCode})</span></p>
                                        <p><span className="text-gray-600">Criado por:</span> <span className="font-medium">{nps.employeeName}</span></p>
                                        <p><span className="text-gray-600">Data:</span> <span className="font-medium">{formatLocalDate(nps.createdAt)}</span></p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">NPS Score</h4>
                                    <div className="flex items-center">
                                        <div className={`nps-score text-xl px-4 py-1 mr-3 rounded ${getBadgeClass(nps.npsScore)}`}>
                                            {nps.npsScore}
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">{getNpsCategory(nps.npsScore)}</p>
                                            <p className="text-xs text-gray-500">Nota: {nps.npsScore}/10</p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-2">Informações do Pedido</h4>
                                    <div className="space-y-2 text-sm">
                                        <p><span className="text-gray-600">Número Pedido:</span> <span className="font-medium">{nps.orderId.substring(4)}</span></p>
                                        <p><span className="text-gray-600">Romaneio/Transporte:</span> <span className="font-medium">{formatRomaneio(nps.orderRomaneioId)}</span></p>
                                        <p><span className="text-gray-600">Motorista:</span> <span className="font-medium">{nps.orderRomaneioDriverId.substring(4) || 'Nome não cadastrado'} - {nps.orderRomaneioDriverName || 'Nome não cadastrado'}</span></p>
                                        <p><span className="text-gray-600">Ajudante 1:</span> <span className="font-medium">{nps.orderRomaneioDriverassistant1Id ? nps.orderRomaneioDriverassistant1Id.substring(4) : 'Não Informado'} {nps.orderRomaneioDriverassistant1Name}</span></p>
                                        <p><span className="text-gray-600">Ajudante 2:</span> <span className="font-medium">{nps.orderRomaneioDriverassistant2Id ? nps.orderRomaneioDriverassistant2Id.substring(4) : 'Não Informado'} {nps.orderRomaneioDriverassistant2Name}</span></p>
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <h4 className="font-medium text-gray-900 mb-2">Classificações de categoria</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        {[
                                            { label: 'Abordagem da Equipe', value: nps.teamApproach, notes: nps.teamApproachNotes },
                                            { label: 'Armazenamento e Conferência', value: nps.storageCheck, notes: nps.storageCheckNotes },
                                            { label: 'Qualidade dos Produtos', value: nps.productQuality, notes: nps.productQualityNotes },
                                            { label: 'Resolução de Avarias e Faltas', value: nps.issueResolution, notes: nps.issueResolutionNotes },
                                            { label: 'Prazo e Horário de Entrega', value: nps.deliveryTime, notes: nps.deliveryTimeNotes }
                                        ].map(({ label, value, notes }) => (
                                            <div key={label} className="border rounded-lg p-3">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium">{label}</span>
                                                    <div className={`rating-badge px-2 py-1 rounded ${getRatingClass(value)}`}>
                                                        {value}
                                                    </div>
                                                </div>
                                                <p className="text-gray-600">{notes || 'Sem observações.'}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <h4 className="font-medium text-gray-900 mb-2">Observação Final</h4>
                                    <div className="border rounded-lg p-4 text-sm text-gray-700">
                                        {nps.finalNotes || 'Sem anotações finais.'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-indigo-300 shadow-sm px-4 py-2 bg-indigo-600 text-white font-medium text-white-700 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={handleExportPDF}
                        >
                            Exportar PDF
                        </button>
                        <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-400 text-white font-medium text-white-700 hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={() => onDelete(nps)}
                        >
                            Deletar NPS
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-3 w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3"
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
